import type { FormInstance, InputRef } from 'antd'
import { Button, Input, message, Popconfirm } from 'antd'
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import { BUYBACK_FIELD, BuybackFormValues } from '../../../common/buybackTypes'
import { useCreateBuybackCalculationMutation } from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { BUYBACK_CALCULATOR_VERSION } from '../../utils/buybackConst'

export interface BuybackFormFooterSaveButtonProps {
  isLastStep: boolean
  form?: FormInstance<BuybackFormValues>
}

export const BuybackFormFooterSaveButton: FunctionComponent<BuybackFormFooterSaveButtonProps> =
  props => {
    const tenantId = useAppSelector(state => state.myProfile.tenantId)
    const userID = useAppSelector(state => state.myProfile.userId)
    const ruianData = useAppSelector(state => state.property.ruian?.data)
    const inputRef = useRef<InputRef>(null)
    const saveButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(
      null
    )

    const [name, setName] = useState<string | undefined>()
    const [disabled] = useState(false)
    const [confirmLoading, setConfirmLoading] = useState(false)
    const [, createBuybackCalculation] = useCreateBuybackCalculationMutation()

    const saveCalculation = useCallback(async () => {
      if (!props.form) return
      const data = props.form.getFieldsValue(true) as Partial<BuybackFormValues>

      setConfirmLoading(true)
      try {
        const result = await createBuybackCalculation({
          buybackCalculation: {
            formData: JSON.stringify(data),
            propertyType: ruianData?.type || '',
            propertyName: data?.address || ruianData?.nazev || '',
            propertyId: ruianData?.id || '',
            accountId: tenantId,
            accountUserId: userID,
            cadastralCode: String(data?.cadastralAreaCode),
            description: data?.[BUYBACK_FIELD.Note],
            priceEstimateId: data?.[BUYBACK_FIELD.PriceEstimateId],
            title: name,
            lv: String(data?.lvNumber),
            calculatorVersion: BUYBACK_CALCULATOR_VERSION,
          },
        })

        const calculationId =
          result.data?.createBuybackCalculation?.buybackCalculation?.id
        if (!calculationId) throw new Error('Nepodařilo se uložit kalkulaci')

        try {
          const pdfResponse = await fetch(`/api/buyback/pdf/${calculationId}`)
          if (!pdfResponse.ok) throw new Error('Nepodařilo se vygenerovat PDF')
        } catch (pdfError) {
          message.warning(
            'Kalkulace uložena, ale PDF se nepodařilo vygenerovat'
          )
        }
        message.success('Kalkulace byla uložena')
      } catch (error) {
        message.error('Nepodařilo se uložit kalkulaci')
      } finally {
        setConfirmLoading(false)
      }
      return
    }, [
      createBuybackCalculation,
      name,
      ruianData?.id,
      ruianData?.nazev,
      ruianData?.type,
      props.form,
      tenantId,
      userID,
    ])

    const handleChange = useCallback(
      (event: { target: { value?: string } }) => {
        setName(event.target.value)
      },
      []
    )

    const handleKeyDown = useCallback(
      async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && name) {
          event.preventDefault()
          await saveCalculation()
          saveButtonRef.current?.click()
        }
      },
      [name, saveCalculation]
    )

    const onOpenChange = useCallback((open: boolean) => {
      if (open) {
        setTimeout(() => inputRef.current?.focus?.(), 0)
      }
    }, [])

    const buttonProps = useMemo(
      () => ({
        cancel: { size: 'middle' as const },
        ok: {
          loading: confirmLoading,
          disabled: !name,
          size: 'middle' as const,
        },
      }),
      [confirmLoading, name]
    )

    return (
      <Popconfirm
        title="Uložit kalkulaci"
        description={
          <Input
            ref={inputRef}
            onChange={handleChange}
            placeholder="Název kalkulace"
            disabled={disabled}
            className="!w-64"
            onKeyDown={handleKeyDown}
          />
        }
        onConfirm={saveCalculation}
        onOpenChange={onOpenChange}
        okButtonProps={buttonProps.ok}
        cancelButtonProps={buttonProps.cancel}
        icon={null}
        autoAdjustOverflow
        okText="Uložit"
        disabled={disabled}
      >
        <Button
          disabled={disabled}
          type="primary"
          ghost={!props.isLastStep}
          ref={saveButtonRef}
        >
          Uložit kalkulaci
        </Button>
      </Popconfirm>
    )
  }
